import { FilterOptionEntity } from '@/Enums/Filters/FilterOptionEntity.enum';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IFilterState } from '@/Interfaces/Filters/FilterState.interface';
import { mapWhitelabelToControlOption } from '../Whitelabels';
import {
  isAdminRole,
  isAdvertiserRole,
  isAgencyRole,
  isClientRole,
  isOrgAdminRole,
  isSuperAdminRole,
  isWhitelabelAdminRole,
} from '../roles';

export const getWhitelabelOptions = ({ userRole, whitelabels }: IFilterState): IWhitelabelControlOption[] => {
  if (isOrgAdminRole(userRole) || isSuperAdminRole(userRole)) {
    return whitelabels?.map(mapWhitelabelToControlOption) || [];
  }
  return [];
};

export const getClientOptions = ({ userRole, clients, selectedWhitelabels }: IFilterState): IClientControlOption[] => {
  if (isSuperAdminRole(userRole) || isOrgAdminRole(userRole)) {
    return (selectedWhitelabels || []).flatMap(wl => wl.clients) || [];
  }

  if (isClientRole(userRole) || isWhitelabelAdminRole(userRole)) {
    return clients || [];
  }

  return [];
};

const getAdvertiserOptions = ({
  userRole,
  clients,
  whitelabels,
  selectedClients,
  selectedWhitelabels,
  options,
}: IFilterState): IControlOption[] => {
  if (
    options?.prefetchAdvertisers &&
    !selectedClients?.length &&
    !selectedWhitelabels?.length &&
    isAdminRole(userRole)
  ) {
    return (
      (whitelabels || [])?.flatMap(whitelabel =>
        (whitelabel?.clients || [])?.flatMap(client => client?.advertisers || []),
      ) || []
    );
  }

  if (isAgencyRole(userRole) || isAdvertiserRole(userRole)) {
    return clients?.[0]?.advertisers || [];
  }

  return (selectedClients || [])?.flatMap(client => client?.advertisers || []) || [];
};

const OptionGetters: Record<
  FilterOptionEntity,
  typeof getWhitelabelOptions | typeof getClientOptions | typeof getAdvertiserOptions
> = {
  [FilterOptionEntity.Whitelabel]: getWhitelabelOptions,
  [FilterOptionEntity.Client]: getClientOptions,
  [FilterOptionEntity.Advertiser]: getAdvertiserOptions,
};

export const getEntityFilterOptions = (
  entity: FilterOptionEntity,
  filterState: IFilterState,
): IWhitelabelControlOption[] | IControlOption[] | IClientControlOption[] => {
  const optionGetter = OptionGetters[entity];
  if (!optionGetter) {
    return [];
  }
  const options = optionGetter(filterState);
  return [...options]?.sort((a, b) => (a.label || '').localeCompare(b.label || '')) || [];
};

interface IFilterControlOptions<AType = IControlOption> {
  [FilterOptionEntity.Whitelabel]: IWhitelabelControlOption<AType>[];
  [FilterOptionEntity.Client]: IClientControlOption<AType>[];
  [FilterOptionEntity.Advertiser]: AType[];
}

export function getFilterOptions<AType = IControlOption>(filterState: IFilterState): IFilterControlOptions<AType> {
  return {
    [FilterOptionEntity.Whitelabel]: getEntityFilterOptions(
      FilterOptionEntity.Whitelabel,
      filterState,
    ) as IWhitelabelControlOption<AType>[],
    [FilterOptionEntity.Client]: getEntityFilterOptions(
      FilterOptionEntity.Client,
      filterState,
    ) as IClientControlOption<AType>[],
    [FilterOptionEntity.Advertiser]: getEntityFilterOptions(FilterOptionEntity.Advertiser, filterState) as AType[],
  };
}
